import Vue from "vue";
import DataRiwayat from "@/services/DataRiwayat";
import RiwayatPemeriksaan from "@/services/RiwayatPemeriksaan";
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";

import store from "@/store";
import {
  ListRiwayat,
  PaginatorMeta,
  RiwayatDetailEntry,
  RiwayatDrawerRow,
  RiwayatIterItem,
  RiwayatRegisterItem
} from "@/types/riwayat";
import {
  RIWAYAT_PER_PAGE,
  asArray,
  gabungBarisRiwayat,
  mergeRegisterPage,
  normaliseDetailBundle,
  pesanGagal
} from "@/utils/riwayatDrawer";

let epoch = 0;
let listRiwayatToken = 0;

@Module({ store, dynamic: true, namespaced: true, name: "riwayat-store" })
class RiwayatStore extends VuexModule {
  listriwayat: ListRiwayat[] = [];
  loadingListRiwayat = false;
  rm: string | null = null;
  items: RiwayatRegisterItem[] = [];
  iters: RiwayatIterItem[] = [];
  page = 0;
  lastPage = 0;
  total = 0;
  loadingList = false;
  listError: string | null = null;
  detailByRegister: Record<string, RiwayatDetailEntry> = {};

  get hasMore(): boolean {
    return this.page > 0 && this.page < this.lastPage;
  }

  get rows(): RiwayatDrawerRow[] {
    return gabungBarisRiwayat(
      this.items,
      this.iters,
      this.page > 0 && this.page >= this.lastPage
    );
  }

  @Mutation SET_LIST_RIWAYAT(listriwayat: ListRiwayat[]) {
    this.listriwayat = listriwayat;
  }

  @Mutation SET_LOADING_LIST_RIWAYAT(loading: boolean) {
    this.loadingListRiwayat = loading;
  }

  @Mutation RESET_DRAWER(rm: string | null) {
    epoch += 1;
    this.rm = rm;
    this.items = [];
    this.iters = [];
    this.page = 0;
    this.lastPage = 0;
    this.total = 0;
    this.loadingList = false;
    this.listError = null;
    this.detailByRegister = {};
  }

  @Mutation SET_LOADING_LIST(loading: boolean) {
    this.loadingList = loading;
  }

  @Mutation SET_LIST_ERROR(message: string | null) {
    this.listError = message;
  }

  @Mutation APPEND_PAGE(payload: { items: unknown; meta: PaginatorMeta }) {
    this.items = mergeRegisterPage(this.items, payload.items);
    this.page = payload.meta.current_page;
    this.lastPage = payload.meta.last_page;
    this.total = payload.meta.total;
  }

  @Mutation SET_ITERS(iters: RiwayatIterItem[]) {
    this.iters = iters;
  }

  @Mutation SET_DETAIL_ENTRY(payload: { key: string; entry: RiwayatDetailEntry }) {
    Vue.set(this.detailByRegister, payload.key, payload.entry);
  }

  @Action({ rawError: true })
  async act_list(param: any): Promise<void> {
    const token = ++listRiwayatToken;
    this.SET_LOADING_LIST_RIWAYAT(true);

    try {
      const dt = await DataRiwayat.getDataRiwayatRm(param["rm"], param["expand"]);
      if (token !== listRiwayatToken) return;

      this.SET_LIST_RIWAYAT(Array.isArray(dt?.data) ? dt.data : []);
    } finally {
      if (token === listRiwayatToken) this.SET_LOADING_LIST_RIWAYAT(false);
    }
  }

  @Action resetForPasien() {
    listRiwayatToken += 1;
    this.RESET_DRAWER(null);
    this.SET_LIST_RIWAYAT([]);
    this.SET_LOADING_LIST_RIWAYAT(false);
  }

  @Action({ rawError: true })
  async ensureList(rm: string): Promise<void> {
    if (this.rm === rm && (this.page > 0 || this.loadingList)) return;

    if (this.rm !== rm) this.RESET_DRAWER(rm);

    await this.loadListPage(1);
  }

  @Action({ rawError: true })
  async loadListPage(page: number): Promise<void> {
    const rm = this.rm;
    if (!rm || this.loadingList) return;

    const token = epoch;
    this.SET_LOADING_LIST(true);
    this.SET_LIST_ERROR(null);

    try {
      const iterReq =
        page === 1 ? RiwayatPemeriksaan.iterObat(rm).catch(() => null) : null;
      const resp = await RiwayatPemeriksaan.listRegister(rm, page, RIWAYAT_PER_PAGE);
      const iters = await iterReq;
      if (token !== epoch) return;

      if (iters) this.SET_ITERS(asArray<RiwayatIterItem>(iters));

      const rows = asArray<RiwayatRegisterItem>(resp?.data);
      const meta: PaginatorMeta = resp?.meta ?? {
        current_page: page,
        per_page: RIWAYAT_PER_PAGE,
        total: rows.length,
        last_page: page
      };

      this.APPEND_PAGE({ items: rows, meta });
    } catch (e) {
      if (token !== epoch) return;

      this.SET_LIST_ERROR(pesanGagal(e, "Gagal memuat riwayat pemeriksaan"));
    } finally {
      if (token === epoch) this.SET_LOADING_LIST(false);
    }
  }

  @Action({ rawError: true })
  async loadMore(): Promise<void> {
    if (this.loadingList || this.page === 0 || this.page >= this.lastPage) return;

    await this.loadListPage(this.page + 1);
  }

  @Action({ rawError: true })
  async loadDetail(register: string | number): Promise<void> {
    const key = String(register);
    const current = this.detailByRegister[key];
    if (current && current.status !== "error") return;

    const token = epoch;
    this.SET_DETAIL_ENTRY({
      key,
      entry: { status: "loading", bundle: null, error: null }
    });

    try {
      const raw = await RiwayatPemeriksaan.detail(key);
      if (token !== epoch) return;

      this.SET_DETAIL_ENTRY({
        key,
        entry: { status: "loaded", bundle: normaliseDetailBundle(raw), error: null }
      });
    } catch (e) {
      if (token !== epoch) return;

      this.SET_DETAIL_ENTRY({
        key,
        entry: {
          status: "error",
          bundle: null,
          error: pesanGagal(e, "Gagal memuat detail riwayat")
        }
      });
    }
  }
}

export const storeRiwayat = getModule(RiwayatStore);
